import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Header.scss";
import {
  MotifHeader,
  MotifHeaderLogo,
  MotifAvatar,
  MotifVerticalNavigationHeaderButton,
  MotifDropdownItem,
  MotifDropdown,
} from "@ey-xd/motif-react";
import { useDispatch, useSelector } from "react-redux";
import { getPhoto } from "../../store/slices/userSlice";
import { fetchRoles } from "../../store/slices/landingPageSlice";
import { appName, appSubTitle } from "../../util/constants";
import { routes } from "../../routes/routes.constatns";
import { useMsal } from "@azure/msal-react";
import { useGetTokenForImage } from "../../hooks/useAppToken";
import { LanguageDropdown } from "../LanguageDropdown/LanguageDropdown";
import { useLocation } from "react-router-dom";
import {
  normalizeRoles,
  isResourceManager,
  isEngagementManager,
  ROLES,
} from "../../util/roleHelpers";

function Avatar({ user, avatar, role, isHomePage = false }) {
  const [isDropdownOpen, setDropdown] = useState(false);
  const { instance } = useMsal();
  const triggerId = "dropdown-trigger-1";

  const handleClickDropdownItem = async () => {
    instance.logoutRedirect({
      account: instance.getActiveAccount(),
      postLogoutRedirectUri: `${process.env.REACT_APP_URL_REG_ALL}${routes.home}`,
    });
  };

  return (
    <MotifDropdown
      ariaLabelledby={triggerId}
      handleOnClose={() => setDropdown(false)}
      onClick={() => setDropdown(!isDropdownOpen)}
      open={isDropdownOpen}
      placement="left"
      trigger={
        <MotifAvatar
          description={user.name}
          size="medium"
          userName={user.name}
          src={avatar}
          interactive={true}
          id={triggerId}
        />
      }
    >
      <MotifDropdownItem
        indexItem={1}
        aria-label="Log out"
        onClick={handleClickDropdownItem}
        type="container"
      >
        <div
          style={{
            display: "flex",
            gap: "12px",
          }}
        >
          <MotifAvatar
            description={user.name}
            size="large"
            userName={user.name}
            src={avatar}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span className="motif-body1-default-bold">{user.name}</span>
            <span className="motif-body1-default-light">{user.username}</span>
            {role && !isHomePage && (
              <span className="header-user-role">{role}</span>
            )}
          </div>
        </div>
      </MotifDropdownItem>
      <span className="motif-dropdown-horizontal-divider" />
      <MotifDropdownItem
        indexItem={2}
        aria-label="Logout"
        onClick={handleClickDropdownItem}
      >
        Logout
      </MotifDropdownItem>
    </MotifDropdown>
  );
}

function Header({ setSideMenuCollapsed = () => {}, isHomePage = false }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state) => state.user.user);
  const avatar = useSelector((state) => state.user.photo);
  const roleData = useSelector((state) => state.landing.roleData);
  const isRoleLoading = useSelector((state) => state.landing.isRoleLoading);
  const token = useGetTokenForImage();
  const userEmail = user?.username || user?.email || "";
  const lastRequestedRoleEmailRef = useRef("");

  const domainFullNames = {
    ADN: "Assurance Delivery Network",
    AOA: "Assurance Operational Accelerator",
  };
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const currentDomain = ["ADN", "AOA"].includes(pathSegments[0]?.toUpperCase())
    ? pathSegments[0].toUpperCase()
    : null;
  const headerSubtitle = currentDomain
    ? `${appSubTitle} | ${domainFullNames[currentDomain]}`
    : appSubTitle;

  const roles = useMemo(() => normalizeRoles(roleData?.role), [roleData?.role]);
  const hasRoles = roles.length > 0;
  const coeLabel = String(roleData?.coe || "").toUpperCase();

  const currentRoute = pathSegments[pathSegments.length - 1] || "";
  const isOnResourceManagerPage = currentRoute === routes.resourceManager;
  const isOnEngagementManagerPage = currentRoute === routes.engagementManager;

  const userIsResourceManager = isResourceManager(roles);
  const userIsEngagementManager = isEngagementManager(roles);

  const resourceManagerDisplay =
    coeLabel === "AOA" ? "PMO" : "Resource Manager";

  let displayRoleLabel = "";
  if (isOnResourceManagerPage && userIsResourceManager) {
    displayRoleLabel = resourceManagerDisplay;
  } else if (isOnEngagementManagerPage && userIsEngagementManager) {
    displayRoleLabel = ROLES.ENGAGEMENT_MANAGER;
  } else if (userIsResourceManager && userIsEngagementManager) {
    displayRoleLabel = `${resourceManagerDisplay}, ${ROLES.ENGAGEMENT_MANAGER}`;
  } else if (userIsResourceManager) {
    displayRoleLabel = resourceManagerDisplay;
  } else if (userIsEngagementManager) {
    displayRoleLabel = ROLES.ENGAGEMENT_MANAGER;
  } else if (hasRoles) {
    displayRoleLabel = roles.join(", ");
  }
  async function onLogoClickHandler() {
    window.location.href = `${process.env.REACT_APP_URL_REG_ALL}${routes.home}`;
  }

  useEffect(() => {
    if (user) {
      if (!avatar && token) dispatch(getPhoto({ token: token }));
    }
  }, [dispatch, user, avatar, token]);

  useEffect(() => {
    if (!userEmail || hasRoles || isRoleLoading) return;
    if (lastRequestedRoleEmailRef.current === userEmail) return;

    lastRequestedRoleEmailRef.current = userEmail;
    dispatch(fetchRoles({ email: userEmail }));
  }, [dispatch, userEmail, hasRoles, isRoleLoading]);

  return (
    <MotifHeader
      className="app-header"
      appHeaderName={
        <div className="app-header-name-wrap">
          <span className="app-header-name">{appName}</span>
          <span className="app-header-subtitle">{headerSubtitle}</span>
        </div>
      }
      logo={
        <MotifHeaderLogo>
          <a
            href="/"
            aria-label="EY Logo - Home"
            onClick={() => {
              return onLogoClickHandler();
            }}
            className="app-header-logo"
          >
            <img
              aria-hidden="true"
              alt="LOGO"
              src={require("../../assets/Images/EY_Logo_Beam_RGB_White_Yellow.png")}
            />
          </a>
        </MotifHeaderLogo>
      }
      iconsContainer={
        <div className="app-header-right-components">
          {!isHomePage && <LanguageDropdown />}
          <div className="user-info-section">
            <span className="motif-body2-default-regular">{user.name}</span>
            {displayRoleLabel && !isHomePage && (
              <span className="header-user-role">{displayRoleLabel}</span>
            )}
          </div>
          <Avatar
            user={user}
            avatar={avatar}
            role={displayRoleLabel}
            isHomePage={isHomePage}
          />
        </div>
      }
      leftComponents={
        !isHomePage && (
          <div className="app-header-left-components">
            <MotifVerticalNavigationHeaderButton
              onClick={setSideMenuCollapsed}
            />
          </div>
        )
      }
    />
  );
}

export default Header;
